// =============================================
// EAV Refresh Guard — ZASADA (2026-08-24):
// odswiezenie ctx.knowledge.eav NIGDY nie zubaza kontekstu.
//
// ROOT CAUSE incydentu 21-24.08: re-fetch po Micro-EAV robil
// .in("entity_id", [WSZYSTKIE encje mapy]) — Whiteever ma 968 encji, czyli
// ~37KB samych UUID-ow w URL-u -> serwer odrzucal request -> blad byl
// POLYKANY -> ctx.knowledge.eav = [] -> fan-out/outline/draft bez EAV ->
// tekst bez liczb -> 0 twierdzen -> BRAK ZRODEL (i pokusa fabrykacji).
// 61/62 artykulow od 21.08 mialo puste EAV we wszystkich blokach draftu.
//
// Guard: blad odswiezania LUB pusta lista przy niepustym stanie -> zostaja
// stare dane + warning w logu publicznym. Nowe atrybuty sa DOKLADANE
// (merge z dedupe), nigdy nie zastepuja calosci.
// =============================================

export interface EavRefreshResult<T> {
  eav: T[];
  /** ile nowych atrybutow doklejono */
  merged: number;
  /** true = guard zadzialal (zachowano stare zamiast nadpisania) */
  kept: boolean;
  /** komunikat do logPublic, gdy guard zadzialal */
  warning?: string;
}

export function mergeFreshEav<T extends { entity_name: string; attribute_name: string; value: string }>(
  current: T[],
  fresh: T[] | null | undefined,
  opts?: { error?: unknown; source?: string },
): EavRefreshResult<T> {
  const src = opts?.source || "eav-refresh";
  const cur = current || [];

  if (opts?.error) {
    const msg = String((opts.error as { message?: string })?.message ?? opts.error).slice(0, 140);
    return {
      eav: cur,
      merged: 0,
      kept: true,
      warning: `${src}: błąd odświeżania EAV — zachowuję ${cur.length} istniejących atrybutów (guard). Błąd: ${msg}`,
    };
  }

  if (!fresh || fresh.length === 0) {
    if (cur.length > 0) {
      return {
        eav: cur,
        merged: 0,
        kept: true,
        warning: `${src}: odświeżenie zwróciło 0 EAV — zachowuję ${cur.length} istniejących (guard: pusta lista nigdy nie nadpisuje niepustej)`,
      };
    }
    return { eav: cur, merged: 0, kept: false };
  }

  // MERGE: doklej nowe, bez duplikatow (entity+attribute+value, case-insensitive)
  const keyOf = (a: T) => `${a.entity_name}${a.attribute_name}${a.value}`.toLowerCase();
  const seen = new Set(cur.map(keyOf));
  const added: T[] = [];
  for (const a of fresh) {
    if (!a?.entity_name || !a?.attribute_name) continue;
    const k = keyOf(a);
    if (seen.has(k)) continue;
    seen.add(k);
    added.push(a);
  }
  return { eav: [...cur, ...added], merged: added.length, kept: false };
}
